import { useEffect, useState } from "react";
import moment from "moment";
import { Row } from "react-bootstrap";
import { useFormContext } from "react-hook-form";
import { FormattedMessage } from "react-intl";

import Alert from "components/molecules/Alert/Alert";
import TimeInput from "components/atoms/TimeInput/TimeInput";
import PickupDeliveryDate from "pages/shipper/RequestForm/components/Schedule/PickupDeliveryDate";
import { ICountries } from "pages/shipper/RequestForm/components/Schedule/Types";
import { IQuotationData } from "pages/provider/QuotationDetail/QuotationType";
import usePermissions from "hooks/usePermissions";
import { getTimezoneOffset } from "helpers/dateTimeHelper";

interface IConfirmationTimerProps {
  quoteRequestDetails: IQuotationData;
  countries: ICountries[];
  isMultiStop?: boolean;
}

function ConfirmationTimer(props: IConfirmationTimerProps) {
  const { quoteRequestDetails, countries, isMultiStop } = props;
  const leftColWidth = isMultiStop ? "w-300" : "w-200";
  const { confirmation_date, confirmation_time } = quoteRequestDetails?.organization || {};
  const { permissions } = usePermissions();
  const { preferred_timezone } = JSON.parse(permissions || "{}");

  const [holidays, setHolidays] = useState({});

  const methods = useFormContext();
  const { watch, setValue } = methods;

  const quotationDate = watch("schedule.rfq_deadline_date");
  const pickupTime = watch("schedule.pickup_time");
  const deliveryTime = watch("schedule.delivery_time");
  const confirmationTime = watch("schedule.rfq_deadline_time");
  const isValidQuotationDate = moment(quotationDate).isAfter(confirmation_date);

  useEffect(() => {
    if (pickupTime) {
      setValue("schedule.pickup_time", moment(pickupTime, "HH:mm").format("HH:mm:ss"));
    }
    if (deliveryTime) {
      setValue("schedule.delivery_time", moment(deliveryTime, "HH:mm").format("HH:mm:ss"));
    }
    if (confirmationTime) {
      setValue("schedule.rfq_deadline_time", moment(confirmationTime, "HH:mm").format("HH:mm:ss"));
    }
  }, [watch("schedule")]);

  return (
    <>
      <div className="flex-row mb-3">
        <div className={leftColWidth}>
          <div className={`${leftColWidth} pl-16 pr-16`}>
            <Row className="mb-1">
              <h6>
                <FormattedMessage id="quote.provider.quoteExpiration" defaultMessage="Quote expiration" />
              </h6>
            </Row>
            <Row className="font-b3 text-secondary">
              <p className="font-b2">
                {`${moment(confirmation_date).format("MMMM DD, YYYY")} - ${moment(confirmation_time, "HH:mm:ss").format(
                  "HH:mm",
                )}`}
              </p>
              {preferred_timezone && <p>({getTimezoneOffset(preferred_timezone)})</p>}
            </Row>
          </div>
        </div>
        <div className="flex-row">
          <div className="w-192 align-date">
            <label className="required">
              <FormattedMessage id="quote.provider.quotationValidUntil" defaultMessage="Quotation is valid until" />
            </label>
            <PickupDeliveryDate
              datePickerProps={{
                name: "schedule.rfq_deadline_date",
                required: true,
              }}
              isPickupOrDelivery="pickup"
              countries={countries}
              holidays={holidays}
              setHolidays={setHolidays}
            />
          </div>
          <div className="mt-2">
            <label />
            <TimeInput name="schedule.rfq_deadline_time" required />
          </div>
        </div>
      </div>
      <div className="flex-row">
        <div className={leftColWidth} />
        {quotationDate && !isValidQuotationDate && (
          <Alert variant="danger">
            <FormattedMessage
              id="quote.provider.schedule.quoteWillExpireBefore"
              defaultMessage="Your quote will expire before the customer is going to confirm. Please, give more time."
            />
          </Alert>
        )}
        {isValidQuotationDate && quotationDate && (
          <Alert variant="success">
            <FormattedMessage
              id="quote.provider.schedule.quoteIsValidMoreThanTwoHours"
              defaultMessage="Great! Your quote is valid until more than two hours after the customer is going to confirm."
            />
          </Alert>
        )}
      </div>
    </>
  );
}

export default ConfirmationTimer;
